import React, { useEffect, useRef } from "react";
import { pickColor, rgba } from "./internal/colorUtils";

export const key = "fireflies";

const DEFAULT_BG = "#0a1410";
const DEFAULT_GLOW_A = "#fde68a";
const DEFAULT_GLOW_B = "#86efac";

const FIREFLY_DENSITY = 0.00009; // fireflies per pixel² of canvas area
const MIN_FIREFLIES = 22;
const MAX_FIREFLIES = 80;
const WANDER_FORCE = 0.03;       // random impulse per frame
const MAX_SPEED = 0.6;           // max velocity (px/frame)
const HALO_FRAC = 18;            // halo radius = body radius * this

function rand(min, max) { return min + Math.random() * (max - min); }

export default function Fireflies({ colors = [], style }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const flyRef = useRef([]);
  const colorsRef = useRef([DEFAULT_BG, DEFAULT_GLOW_A, DEFAULT_GLOW_B]);

  useEffect(() => {
    colorsRef.current = [
      pickColor(colors, 0, DEFAULT_BG),
      pickColor(colors, 1, DEFAULT_GLOW_A),
      pickColor(colors, 2, DEFAULT_GLOW_B),
    ];
  }, [colors]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    let width = 0;
    let height = 0;
    const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;
    let t = 0;

    const seedFlies = () => {
      const target = Math.round(width * height * FIREFLY_DENSITY);
      const count = Math.max(MIN_FIREFLIES, Math.min(MAX_FIREFLIES, target));
      const arr = new Array(count);
      for (let i = 0; i < count; i += 1) {
        arr[i] = {
          x: rand(0, width),
          y: rand(0, height),
          vx: rand(-MAX_SPEED * 0.4, MAX_SPEED * 0.4),
          vy: rand(-MAX_SPEED * 0.4, MAX_SPEED * 0.4),
          // Pulsation: each firefly has its own period and phase so the
          // canvas blinks unevenly, like real fireflies.
          phase: rand(0, Math.PI * 2),
          period: rand(1.2, 3.4),
          base: rand(0.18, 0.55),
          peak: rand(0.85, 1.0),
          slot: Math.random() < 0.5 ? 1 : 2,
          radius: rand(1.1, 1.9),
        };
      }
      flyRef.current = arr;
    };

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      // Use clientWidth/Height (layout dims) so an ancestor `transform: scale(...)`
      // (e.g. PreviewDeviceStage) doesn't shrink the canvas and pin it to top-left.
      width = Math.max(1, parent.clientWidth || parent.offsetWidth || 0);
      height = Math.max(1, parent.clientHeight || parent.offsetHeight || 0);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      seedFlies();
    };

    const tick = () => {
      const [bg, glowA, glowB] = colorsRef.current;

      // Faint vertical gradient instead of a flat fill — gives the scene a
      // subtle "horizon" and lets the fireflies feel grounded in space.
      const grad = ctx.createLinearGradient(0, 0, 0, height);
      grad.addColorStop(0, bg);
      grad.addColorStop(1, rgba(bg, 0.85));
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, width, height);

      const flies = flyRef.current;

      // Update positions: random-walk velocity, clamped, with soft wrapping
      // around the edges.
      for (let i = 0; i < flies.length; i += 1) {
        const f = flies[i];
        f.vx += (Math.random() - 0.5) * WANDER_FORCE;
        f.vy += (Math.random() - 0.5) * WANDER_FORCE;
        const speed = Math.sqrt(f.vx * f.vx + f.vy * f.vy);
        if (speed > MAX_SPEED) {
          f.vx = (f.vx / speed) * MAX_SPEED;
          f.vy = (f.vy / speed) * MAX_SPEED;
        }
        f.x += f.vx;
        f.y += f.vy;
        // Soft wrap: when one edge is crossed, reappear on the other side.
        if (f.x < -10) f.x = width + 10;
        if (f.x > width + 10) f.x = -10;
        if (f.y < -10) f.y = height + 10;
        if (f.y > height + 10) f.y = -10;
      }

      // Render in two passes: big soft halos in additive mode (so overlaps
      // brighten), then crisp body dots on top in normal compositing.
      ctx.globalCompositeOperation = "lighter";
      for (let i = 0; i < flies.length; i += 1) {
        const f = flies[i];
        const pulse = f.base + (f.peak - f.base) * (0.5 + 0.5 * Math.sin(t * f.period + f.phase));
        const color = f.slot === 1 ? glowA : glowB;

        const haloR = f.radius * HALO_FRAC;
        const halo = ctx.createRadialGradient(f.x, f.y, 0, f.x, f.y, haloR);
        halo.addColorStop(0, rgba(color, 0.35 * pulse));
        halo.addColorStop(0.4, rgba(color, 0.12 * pulse));
        halo.addColorStop(1, rgba(color, 0));
        ctx.fillStyle = halo;
        ctx.beginPath();
        ctx.arc(f.x, f.y, haloR, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.globalCompositeOperation = "source-over";
      for (let i = 0; i < flies.length; i += 1) {
        const f = flies[i];
        const pulse = f.base + (f.peak - f.base) * (0.5 + 0.5 * Math.sin(t * f.period + f.phase));
        ctx.fillStyle = rgba(f.slot === 1 ? glowA : glowB, pulse);
        ctx.beginPath();
        ctx.arc(f.x, f.y, f.radius, 0, Math.PI * 2);
        ctx.fill();
      }

      t += 0.016;
      rafRef.current = requestAnimationFrame(tick);
    };

    resize();
    rafRef.current = requestAnimationFrame(tick);

    let ro = null;
    if (typeof ResizeObserver !== "undefined" && canvas.parentElement) {
      ro = new ResizeObserver(resize);
      ro.observe(canvas.parentElement);
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", resize);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ro) {
        try { ro.disconnect(); } catch { /* noop */ }
      } else if (typeof window !== "undefined") {
        window.removeEventListener("resize", resize);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        pointerEvents: "none",
        ...style,
      }}
    />
  );
}
